import React from 'react';
import { courses } from '../data/mockData';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Phone, CheckCircle } from 'lucide-react';

const CoursesSection = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="courses" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">Courses</span>
          </h2>
          <div className="w-24 h-1 bg-yellow-400 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive coaching programs designed for your success
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map((course) => {
            const Icon = Icons[course.icon];
            return (
              <div
                key={course.id}
                className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border-4 border-transparent hover:border-blue-400 hover:-translate-y-3 cursor-pointer transform hover:scale-105"
                style={{
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1), 0 0 0 4px rgba(59, 130, 246, 0.1)',
                }}
              >
                {/* Card Header with Background Image */}
                <div className="relative h-48 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${course.bgImage})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 to-blue-700/90"></div>
                  </div>
                  <div className="relative h-full p-6 flex flex-col justify-between">
                    <Icon className="w-12 h-12 text-yellow-400 mb-2" />
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{course.title}</h3>
                      <p className="text-blue-100 font-medium">{course.subtitle}</p>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    {course.description}
                  </p>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">Duration</p>
                        <p className="text-xs text-gray-600">{course.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">Eligibility</p>
                        <p className="text-xs text-gray-600">{course.eligibility}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">Age Criteria</p>
                        <p className="text-xs text-gray-600">{course.ageCriteria}</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={scrollToContact}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 transition-all duration-300 group-hover:bg-yellow-400 group-hover:text-gray-900"
                  >
                    Enquire Now
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* How ZOOM Academy Will Help Section */}
        <div className="mt-20 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl shadow-2xl p-8 md:p-12 border-4 border-yellow-400">
          <div className="text-center mb-10">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              ZOOM ACADEMY आपकी कैसे मदद करेगा?
            </h3>
            <p className="text-xl text-gray-700">हमारी विशेषताएं जो आपको सफलता दिलाएंगी</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600 mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-3">Experienced Faculty</h4>
              <p className="text-gray-700">10+ years experienced teachers with proven track record in competitive exam coaching</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600 mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-3">Study Material</h4>
              <p className="text-gray-700">Comprehensive notes in Hindi & English covering complete syllabus with practice questions</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600 mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-3">Regular Mock Tests</h4>
              <p className="text-gray-700">Weekly mock tests and previous year papers with detailed solution analysis</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600 mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-3">Small Batch Size</h4>
              <p className="text-gray-700">Limited students per batch ensuring personal attention to every student</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600 mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-3">Doubt Sessions</h4>
              <p className="text-gray-700">Daily doubt clearing sessions and one-on-one mentorship for concept clarity</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-green-600 mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-3">Success Record</h4>
              <p className="text-gray-700">1500+ selections with multiple toppers in HAU, NEET, JEE, and government exams</p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:9991129789">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: 99911-29789
                </Button>
              </a>
              <Button onClick={scrollToContact} size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-10 py-6">
                Join Now
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">Can't Find Your Course?</h3>
          <p className="text-xl text-blue-100 mb-6">
            We offer customized coaching for various other competitive exams
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            Contact Us for Details
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;
